import type { ClientSoundPath } from '@game/shared-types';

import { persistentStore } from '@store/persistentStore';

import { playSfx, type SfxKey } from './soundManager';

const soundNames = [
  'UI_TAB_CLICK',
  'UI_CLICK',
  'UI_MENU_CLICK',
  'UI_SUBMENU_CLICK',
  'UI_CLICK_MECHANICAL',
  'UI_CLICK_WARM',
  'UI_DRAG_START',
  'UI_DRAG_END',
  'UI_MOVE_ITEM',
  'CHANGE_LOCATION',
  'PURCHASE_ITEM',
  'SELL_ITEM',
  'GAIN_ITEM',
  'MARKET_ADD_BUY_ORDER',
  'MARKET_ADD_SELL_ORDER',
  'PERFORM_JOB',
  'PERFORM_WORKOUT',
  'ACCEPT_MISSION',
  'COMPLETE_MISSION',
  'EQUIP_ITEM',
  'UNEQUIP_ITEM',
  'USE_ITEM',
  'ARENA_SEARCH',
  'ARENA_ATTACK',
  'CRAFT_ITEM',
  'HOSPITAL_HEAL',
  'SEND_MESSAGE',
  'RECEIVE_MESSAGE',
  'RECEIVE_MAIL',
  'LEARN_SKILL_SUCCESS',
  'LEARN_SKILL_FAILURE',
  'UNLEARN_SKILL',
  'LEARN_TRAIT',
  'PLAYER_DEFEATED',
  'LEVEL_UP',
  'UI_POPUP_APPEAR',
  'UI_POPUP_DISAPPEAR',
] as const;

export type SoundName = (typeof soundNames)[number];

export type SoundCategory = 'ui' | 'combat' | 'gameActions' | 'commerce' | 'chat';

// Central mapping of game actions to sounds
const soundActionMap: Record<SoundName, SfxKey | ClientSoundPath> = {
  // UI interactions
  UI_TAB_CLICK: 'ui_tab_switch',
  UI_CLICK: 'ui_click',
  UI_MENU_CLICK: 'ui_click_soft_wood',
  UI_SUBMENU_CLICK: 'ui_click_soft_wood',
  UI_CLICK_MECHANICAL: 'ui_click_mechanical',
  UI_CLICK_WARM: 'ui_click_sine_pop_warm',
  UI_DRAG_START: 'drag_start',
  UI_DRAG_END: 'drag_end',
  UI_MOVE_ITEM: 'move_item',
  CHANGE_LOCATION: 'ui_zap',
  LEVEL_UP: 'game_fanfare',
  UI_POPUP_APPEAR: 'ui_expand',
  UI_POPUP_DISAPPEAR: 'ui_collapse',

  // Commerce
  PURCHASE_ITEM: 'game_gain_money_register',
  SELL_ITEM: 'game_gain_money_chime',
  GAIN_ITEM: 'game_gain_item_sparkle',
  MARKET_ADD_BUY_ORDER: 'game_gain_money_register',
  MARKET_ADD_SELL_ORDER: 'game_gain_money_jingle',

  // Game actions
  PERFORM_JOB: 'game_perform_job',
  PERFORM_WORKOUT: 'game_use_consumable_crunch',
  ACCEPT_MISSION: 'accept_mission.webm, accept_mission.mp3, accept_mission.m4a',
  COMPLETE_MISSION: 'game_use_consumable_restore',
  EQUIP_ITEM: 'game_equip_item',
  UNEQUIP_ITEM: 'game_unequip_item',
  USE_ITEM: 'game_use_item',
  ARENA_SEARCH: 'game_search_arena',
  ARENA_ATTACK: 'combat_button_click',
  CRAFT_ITEM: 'game_craft_item',
  HOSPITAL_HEAL: 'game_hospital_heal',
  LEARN_SKILL_SUCCESS: 'game_chime',
  LEARN_SKILL_FAILURE: 'game_use_consumable_potion',
  LEARN_TRAIT: 'game_gain_item_warm',
  UNLEARN_SKILL: 'game_gain_item_warm',

  // Combat
  PLAYER_DEFEATED: 'ui_error_buzz',

  // Chat & Communication
  SEND_MESSAGE: 'ui_swoosh_up',
  RECEIVE_MESSAGE: 'ui_plink',
  RECEIVE_MAIL: 'mail_receive',
} as const;

const soundCategoryMap: Record<SoundName, SoundCategory> = {
  // UI interactions
  UI_TAB_CLICK: 'ui',
  UI_CLICK: 'ui',
  UI_MENU_CLICK: 'ui',
  UI_SUBMENU_CLICK: 'ui',
  UI_CLICK_MECHANICAL: 'ui',
  UI_CLICK_WARM: 'ui',
  UI_DRAG_START: 'ui',
  UI_DRAG_END: 'ui',
  UI_MOVE_ITEM: 'ui',
  CHANGE_LOCATION: 'ui',
  LEVEL_UP: 'ui',
  UI_POPUP_APPEAR: 'ui',
  UI_POPUP_DISAPPEAR: 'ui',

  // Commerce
  PURCHASE_ITEM: 'commerce',
  SELL_ITEM: 'commerce',
  GAIN_ITEM: 'commerce',
  MARKET_ADD_BUY_ORDER: 'commerce',
  MARKET_ADD_SELL_ORDER: 'commerce',

  // Game actions
  PERFORM_JOB: 'gameActions',
  PERFORM_WORKOUT: 'gameActions',
  LEARN_SKILL_SUCCESS: 'gameActions',
  LEARN_SKILL_FAILURE: 'gameActions',
  LEARN_TRAIT: 'gameActions',
  UNLEARN_SKILL: 'gameActions',
  ACCEPT_MISSION: 'gameActions',
  COMPLETE_MISSION: 'gameActions',
  EQUIP_ITEM: 'gameActions',
  UNEQUIP_ITEM: 'gameActions',
  USE_ITEM: 'gameActions',
  ARENA_SEARCH: 'gameActions',
  ARENA_ATTACK: 'gameActions',
  CRAFT_ITEM: 'gameActions',
  HOSPITAL_HEAL: 'gameActions',

  // Combat
  PLAYER_DEFEATED: 'combat',

  // Chat & Communication
  SEND_MESSAGE: 'chat',
  RECEIVE_MESSAGE: 'chat',
  RECEIVE_MAIL: 'chat',
} as const;

const categoryEnabledKey: Record<
  SoundCategory,
  'sfxUiEnabled' | 'sfxCombatEnabled' | 'sfxGameActionsEnabled' | 'sfxCommerceEnabled' | 'sfxChatEnabled'
> = {
  ui: 'sfxUiEnabled',
  combat: 'sfxCombatEnabled',
  gameActions: 'sfxGameActionsEnabled',
  commerce: 'sfxCommerceEnabled',
  chat: 'sfxChatEnabled',
} as const;

// Helper function
export function playActionSound(action: SoundName): void {
  const state = persistentStore.getState();
  const category = soundCategoryMap[action];
  if (!state[categoryEnabledKey[category]]) {
    return;
  }
  playSfx(soundActionMap[action]);
}
